import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/previews';
import { productShortName } from '../data/offer';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  return <section className="bg-navy-950 py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-97" data-mp="26a2eog1jbvw9">
      <div className="mx-auto max-w-6xl px-4" data-id="element-98" data-mp="26c5flv4879fr">
        <div className="text-center" data-id="element-99" data-mp="21a6buk4kqaia">
          <p className="text-sm font-extrabold uppercase tracking-[.16em] text-copper" data-id="element-100" data-mp="21b7x1hfb0ddq">Look Inside</p>
          <h2 id="inside-title" className="mx-auto mt-3 max-w-4xl text-balance text-3xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-4xl" data-id="element-101" data-mp="1wdpovt94226s">
            Discover What&apos;s Inside <span className="text-gold" data-id="element-102" data-mp="3qqp5npp892fn">{productShortName}</span>
          </h2>
          <span className="mx-auto mt-4 flex w-fit items-center gap-1" aria-hidden="true" data-id="element-103" data-mp="1ozyjtknlx1mt">
            <span className="h-1 w-10 rounded-full bg-steel" data-id="element-104" data-mp="1tsi3mzcw0t02" />
            <span className="h-1 w-3 rounded-full bg-copper" data-id="element-105" data-mp="1f2u3w2kcu2eg" />
          </span>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-steel-light" data-id="element-106" data-mp="1f0ppursqrn0g">
            Visual pages with clear single-line diagrams, switchgear cutaways, worked examples, technical warnings and
            field-ready takeaways. Here are some real pages from Volume 1.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages" data-id="element-107" data-mp="2cnyg103otk8s">
          <div ref={trackRef} className="flex touch-pan-x snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth px-[10%] pb-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] sm:px-[20%] md:px-[35%] [&::-webkit-scrollbar]:hidden" data-id="element-108" data-mp="22rv5p6yomfar">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-2xl border bg-navy-900 shadow-lg shadow-black/40 transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-copper opacity-100' : 'border-steel/30 opacity-70'}`} aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-109" data-mp="jt72git9k4ut" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" draggable={false} className="block aspect-[4/5] w-full select-none object-cover" data-id="element-110" data-mp="oqp8o1fwc5z4" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className="absolute left-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-steel bg-navy-900 text-white shadow-lg shadow-black/40 transition-colors duration-150 ease-out hover:bg-navy-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 sm:-left-2" aria-label="Previous page" data-id="element-111" data-mp="3s75xni8evm79">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-112" data-mp="39gy6lyhzkm7f" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-steel bg-navy-900 text-white shadow-lg shadow-black/40 transition-colors duration-150 ease-out hover:bg-navy-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 sm:-right-2" aria-label="Next page" data-id="element-113" data-mp="2cpazkba5lo4f">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-114" data-mp="2vk827ilgixa7" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-115" data-mp="30it55ps3fv2c">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-copper ${index === active ? 'w-6 bg-copper' : 'w-2 bg-steel/50 hover:bg-steel'}`} aria-label={`Go to page ${index + 1}`} aria-current={index === active} data-id="element-116" data-mp="34gqtyrhycf57" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}